import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { StyleObj } from '../../../@types';
import { BettingSlipFrom, CashoutReport } from '../../../@types/api';
import { QUERY_KEYS } from '../../../constants';
import { createColumn } from '../../../utils';
import { getData } from '../../../utils/api';
import DateRangeSelect, { DayjsTimestamp } from '../../molecules/DateRangeSelect';
import TableTemplate from '../../templates/TableTemplate';
import { createNumberColumn, createPercentageColumn } from '../../../helpers/columns';

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
};

type CashoutReportQueryParams = {
  fromTimestamp: Dayjs;
  toTimestamp: Dayjs;
  from?: BettingSlipFrom;
  pendingSelections?: number;
  playerIds?: string[];
  cashoutType?: string;
  device?: string;
};

const columns: GridColDef[] = [
  createNumberColumn('coStake', 'CO Stake', {
    sortable: false,
  }),
  createNumberColumn('coPayout', 'CO Payout', {
    sortable: false,
  }),
  createNumberColumn('coProfit', 'CO Profit', {
    sortable: false,
  }),
  createPercentageColumn('coPercentage', 'CO %', {
    sortable: false,
  }),
  createNumberColumn('ifWinnings', 'If Winnings', {
    sortable: false,
  }),
  createNumberColumn('ifProfit', 'If Profit', {
    sortable: false,
  }),
  createPercentageColumn('ifPercentage', 'If %', {
    sortable: false,
  }),
  createNumberColumn('coIfProfit', 'CO/If Profit', {
    sortable: false,
  }),
  createColumn('coIfRatio', 'CO/If Ratio', {
    sortable: false,
  }),
  createColumn('coTickets', 'CO Tickets', {
    sortable: false,
  }),
];

const getMobileCardHeaderItems = (report: CashoutReport) => [
  { label: 'CO Profit', value: Number(report?.coProfit).toFixed(2) },
  { label: 'CO %', value: Number(report?.coPercentage).toFixed(2) + '%', flex: 1 },
  { label: 'CO Tickets', value: report?.coTickets },
];

const CashoutReportsPage = () => {
  const [queryParams, setQueryParams] = useState<CashoutReportQueryParams>({
    fromTimestamp: dayjs().startOf('day'),
    toTimestamp: dayjs().endOf('day'),
  });

  const { data, isFetching, isPending } = useQuery({
    queryKey: [QUERY_KEYS.cashout, queryParams],

    queryFn: (): Promise<CashoutReport> =>
      getData('cashout-report', {
        ...queryParams,
        fromTimestamp: queryParams.fromTimestamp.valueOf(),
        toTimestamp: queryParams.toTimestamp.valueOf(),
      }),
  });

  const handleDateChange = (newDate: DayjsTimestamp) => {
    updateQueryParams(newDate);
  };

  const updateQueryParams = (newParams: Partial<CashoutReportQueryParams>) => {
    setQueryParams((prevValue) => ({ ...prevValue, ...newParams }));
  };

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={data ? [data] : []}
        getRowId={() => 'cashout-report'}
        columns={columns}
        loading={isFetching || isPending}
        changeQuery={updateQueryParams}
        rowCount={data ? 1 : 0}
        mobileCardHeaderItems={getMobileCardHeaderItems}
        customTableHeaderContent={
          <DateRangeSelect
            date={{
              fromTimestamp: queryParams.fromTimestamp,
              toTimestamp: queryParams.toTimestamp,
            }}
            onDateChange={handleDateChange}
          />
        }
        showExport
      />
    </Box>
  );
};

export default CashoutReportsPage;
